import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Empty,
  Icon,
  Spin,
  Tag,
  Typography,
  Upload,
  message,
} from "antd";
import DocumentPreviewViewer from "../../../components/PostVisitDocNotifications/DocumentPreviewViewer";
import {
  fetchPostVisitDocSession,
  filenameMaxLengthForViewport,
  isPostVisitSessionExpiredResponse,
  submitPostVisitDocBatch,
  truncateFilenameWithExt,
  uploadPostVisitFilesWithRetry,
} from "./postVisitDocUpload";
import "./PostVisitDocUploadPage.less";

const { Title, Text, Paragraph } = Typography;

const ACCEPTED = "image/*,.pdf,video/*";
const AUTO_CLOSE_MS = 5000;

function getTokenFromQuery() {
  const params = new URLSearchParams(window.location.search);
  return params.get("token");
}

function useFilenameMaxLength() {
  const compute = () =>
    typeof window !== "undefined" ? filenameMaxLengthForViewport(window.innerWidth) : 36;

  const [maxLength, setMaxLength] = useState(compute);

  useEffect(() => {
    const onResize = () => setMaxLength(compute());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return maxLength;
}

const PostVisitDocUploadPage = () => {
  const token = useMemo(() => getTokenFromQuery(), []);
  const filenameMaxLength = useFilenameMaxLength();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [sessionInfo, setSessionInfo] = useState(null);
  const [files, setFiles] = useState([]);
  const [previewFile, setPreviewFile] = useState(null);
  const [pendingLocalIds, setPendingLocalIds] = useState([]);
  const [expired, setExpired] = useState(false);
  const [expiredReason, setExpiredReason] = useState(null);
  const [submitComplete, setSubmitComplete] = useState(false);

  const fetchSession = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const data = await fetchPostVisitDocSession(token);
      if (data && data.status === 200) {
        setExpired(false);
        setSessionInfo(data.session);
        setFiles(data.files || []);
      } else if (isPostVisitSessionExpiredResponse(data)) {
        setExpired(true);
        setExpiredReason(data.reason || "ticket_closed");
        setSessionInfo(null);
        setFiles([]);
      } else {
        message.error(data?.message || "Invalid upload link");
      }
    } catch (error) {
      const data = error.response && error.response.data;
      if (isPostVisitSessionExpiredResponse(data)) {
        setExpired(true);
        setExpiredReason(data.reason || "ticket_closed");
      } else {
        message.error("Could not load upload session");
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchSession();
  }, [fetchSession]);

  useEffect(() => {
    if (!submitComplete) return undefined;
    const timer = window.setTimeout(() => {
      window.close();
    }, AUTO_CLOSE_MS);
    return () => window.clearTimeout(timer);
  }, [submitComplete]);

  const readyToSubmitIds = useMemo(() => {
    if (pendingLocalIds.length > 0) {
      return pendingLocalIds;
    }
    return files.filter((f) => f.upload_finished && !f.submitted_at).map((f) => f.id);
  }, [pendingLocalIds, files]);

  const hasReadyToSubmit = readyToSubmitIds.length > 0;
  const isBusy = uploading || submitting;

  const uploadFiles = useCallback(
    async (fileList) => {
      if (!token || !fileList.length || expired || submitComplete || isBusy) return;
      setUploading(true);
      setUploadProgress({ current: 0, total: fileList.length, name: "" });
      try {
        const result = await uploadPostVisitFilesWithRetry(
          token,
          fileList,
          2,
          (current, total, name) => {
            setUploadProgress({ current, total, name });
          }
        );
        if (result.success && result.fileIds.length) {
          setPendingLocalIds((prev) => [...prev, ...result.fileIds]);
          await fetchSession();
        }
      } catch (error) {
        const data = error.response && error.response.data;
        if (isPostVisitSessionExpiredResponse(data)) {
          setExpired(true);
          setExpiredReason(data.reason || "ticket_closed");
          message.error("Upload link expired");
        } else {
          console.error(error);
          message.error("Upload failed. Please try again.");
        }
      } finally {
        setUploading(false);
        setUploadProgress(null);
      }
    },
    [token, fetchSession, expired, submitComplete, isBusy]
  );

  const handleSubmit = useCallback(async () => {
    if (!token || expired || submitComplete || isBusy || !readyToSubmitIds.length) {
      return;
    }
    setSubmitting(true);
    try {
      const res = await submitPostVisitDocBatch(token, readyToSubmitIds);
      if (res?.status === 200) {
        setPendingLocalIds([]);
        setSubmitComplete(true);
      } else {
        message.error(res?.message || "Submit failed");
      }
    } catch (error) {
      const data = error.response && error.response.data;
      if (isPostVisitSessionExpiredResponse(data)) {
        setExpired(true);
        setExpiredReason(data.reason || "ticket_closed");
        message.error("Upload link expired");
      } else {
        message.error("Submit failed");
      }
    } finally {
      setSubmitting(false);
    }
  }, [token, readyToSubmitIds, expired, submitComplete, isBusy]);

  const handleSelectFile = useCallback(
    (item) => {
      if (isBusy) return;
      setPreviewFile(item);
    },
    [isBusy]
  );

  const renderOverlay = () => {
    if (!isBusy) return null;
    let label = "Please wait…";
    if (submitting) {
      label = "Submitting documents to coordinator…";
    } else if (uploadProgress && uploadProgress.total > 0) {
      label = `Uploading ${uploadProgress.current} of ${uploadProgress.total}${
        uploadProgress.name ? `: ${uploadProgress.name}` : ""
      }`;
    } else if (uploading) {
      label = "Uploading files…";
    }
    return (
      <div className="pv-upload-overlay" role="status" aria-live="polite">
        <Spin size="large" />
        <Text className="pv-upload-overlay-text">{label}</Text>
      </div>
    );
  };

  if (!token) {
    return (
      <div className="pv-upload-page">
        <Card className="pv-upload-card">
          <Empty description="Missing upload token in link" />
        </Card>
      </div>
    );
  }

  if (expired) {
    return (
      <div className="pv-upload-page">
        <Card className="pv-upload-card" bordered={false}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <div>
                <Title level={4}>Upload link expired</Title>
                <Paragraph type="secondary">
                  {expiredReason === "ticket_closed"
                    ? "This ticket is closed. Documents can no longer be uploaded through this link."
                    : "This upload link is no longer active."}
                </Paragraph>
              </div>
            }
          />
        </Card>
      </div>
    );
  }

  if (submitComplete) {
    return (
      <div className="pv-upload-page">
        <Card className="pv-upload-card pv-upload-thankyou" bordered={false}>
          <div className="pv-upload-thankyou-inner">
            <Icon type="check-circle" className="pv-upload-thankyou-icon" />
            <Title level={3}>Thank you</Title>
            <Paragraph>
              Your documents have been submitted to the coordinator successfully.
            </Paragraph>
            <Paragraph type="secondary">
              This window will close automatically. You may close it manually if it does not.
            </Paragraph>
            <Paragraph type="secondary" className="pv-upload-thankyou-note">
              To upload more documents later, use the same link from your WhatsApp message until
              the ticket is closed.
            </Paragraph>
            <Button type="primary" onClick={() => window.close()}>
              Close
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="pv-upload-page">
      {renderOverlay()}
      <Card className="pv-upload-card" bordered={false}>
        {loading ? (
          <div className="pv-upload-loading">
            <Spin size="large" />
          </div>
        ) : (
          <>
            <div className="pv-upload-header">
              <Title level={3} className="pv-upload-title">
                Upload visit documents
              </Title>
              {sessionInfo && (
                <div className="pv-upload-meta">
                  <Tag color="blue">{sessionInfo.project_name}</Tag>
                  <Text strong>{sessionInfo.cti || sessionInfo.ticket_number}</Text>
                  {sessionInfo.visit_number ? (
                    <Text type="secondary">Visit {sessionInfo.visit_number}</Text>
                  ) : null}
                </div>
              )}
              <Paragraph type="secondary" className="pv-upload-hint">
                Upload photos, PDFs, or videos together. You can return to this link later to add
                more files until the ticket is closed. Files cannot be deleted from here.
              </Paragraph>
            </div>

            <Upload.Dragger
              multiple
              accept={ACCEPTED}
              showUploadList={false}
              beforeUpload={(file, fileList) => {
                uploadFiles(fileList.slice(0, 20));
                return false;
              }}
              disabled={isBusy}
              className="pv-upload-dragger"
            >
              <p className="ant-upload-drag-icon">
                <Icon type="inbox" />
              </p>
              <p className="ant-upload-text">Tap or drag files here</p>
              <p className="ant-upload-hint">Images, PDF, video — up to 20 files per batch</p>
            </Upload.Dragger>

            <div className="pv-upload-body">
              <div className="pv-upload-list-pane">
                <Title level={4} className="pv-upload-section-title">
                  Uploaded files
                </Title>
                {files.length === 0 ? (
                  <Text type="secondary">No files yet</Text>
                ) : (
                  <div className="pv-upload-file-list">
                    {files.map((item) => (
                      <div
                        key={item.id}
                        className={
                          previewFile && previewFile.id === item.id
                            ? "pv-upload-file-row active"
                            : "pv-upload-file-row"
                        }
                      >
                        <button
                          type="button"
                          className="pv-upload-file-main"
                          onClick={() => handleSelectFile(item)}
                          disabled={isBusy}
                        >
                          <span className="pv-upload-file-name" title={item.original_name}>
                            {truncateFilenameWithExt(item.original_name, filenameMaxLength)}
                          </span>
                          <div className="pv-upload-file-tags">
                            {item.submitted_at ? (
                              <Tag color="green">Submitted</Tag>
                            ) : item.upload_finished ? (
                              <Tag color="gold">Ready to submit</Tag>
                            ) : (
                              <Tag>Uploading</Tag>
                            )}
                            {item.mapped_document_type ? (
                              <Tag>{item.mapped_document_type}</Tag>
                            ) : null}
                          </div>
                        </button>
                        <Button
                          type="link"
                          icon="eye"
                          className="pv-upload-file-preview-btn"
                          aria-label={`Preview ${item.original_name}`}
                          onClick={() => handleSelectFile(item)}
                          disabled={isBusy || !item.preview_url}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="pv-upload-preview-pane">
                <DocumentPreviewViewer
                  file={previewFile}
                  compact
                  emptyLabel="Select a file to preview"
                />
              </div>
            </div>

            {hasReadyToSubmit && (
              <div className="pv-upload-actions">
                <Button
                  type="primary"
                  size="large"
                  block
                  loading={submitting}
                  onClick={handleSubmit}
                  disabled={isBusy}
                >
                  Submit to coordinator
                </Button>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
};

export default PostVisitDocUploadPage;
